import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as Logo } from '../logo.svg';
import { authApi } from '../services/api';

export default function EmployeeLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = (await authApi.login({ email, password })) as {
        access_token?: string;
        user?: { user_type?: string };
        message?: string;
        errors?: { email?: string[] };
      };
      if (res.access_token) {
        const isEmployee = res.user?.user_type === 'employee';
        navigate(isEmployee ? '/employee' : '/dashboard');
      } else {
        setError(res.message || res.errors?.email?.[0] || 'Login failed');
      }
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white text-gray-900">
      <motion.div
        className="w-full lg:w-[52%] flex flex-col justify-center items-center px-4 sm:px-10 lg:px-16 xl:px-24 py-8 sm:py-12"
        initial={{ opacity: 0, x: -24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full max-w-[400px] flex flex-col items-start">
          <motion.div
            className="flex flex-col gap-4 mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 w-fit text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors group"
            >
              <span className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 group-hover:border-brand-300 group-hover:text-brand-600 group-hover:bg-brand-50/50 transition-colors shadow-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </span>
              Back to home
            </Link>
            <Link to="/" className="flex items-center gap-2 w-fit">
              <span className="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center text-white shadow-lg shadow-brand-600/30">
                <Logo className="w-5 h-5 shrink-0" aria-hidden />
              </span>
              <span className="font-bold text-xl text-gray-900">SpynERP</span>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
              HR & Payroll — Time in / Time out
            </h1>
            <p className="text-gray-600 mb-8">
              Sign in with your company email to clock in or out.
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-5 w-full">
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <label htmlFor="emp-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="emp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none transition-all"
                autoComplete="email"
                required
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label htmlFor="emp-password" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Password
              </label>
              <input
                id="emp-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none transition-all"
                autoComplete="current-password"
                required
              />
            </motion.div>
            <motion.button
              type="submit"
              className={`w-full rounded-xl bg-brand-600 px-4 py-3.5 text-white font-semibold shadow-lg shadow-brand-600/25 hover:bg-brand-700 hover:shadow-brand-600/35 transition-all focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </motion.button>
          </form>

          <motion.p
            className="mt-8 text-sm text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Are you a merchant or admin?{' '}
            <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-700">
              Sign in here →
            </Link>
          </motion.p>
        </div>
      </motion.div>

      <motion.div
        className="hidden lg:flex lg:w-[48%] relative overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 items-center justify-center p-12 xl:p-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          }}
        />
        <div className="relative z-10 max-w-md w-full text-center">
          <motion.div
            className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-8 shadow-xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">HR & Payroll</h2>
            <p className="text-brand-100 text-sm leading-relaxed">
              Sign in to record your time in and time out for the day.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
